import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';
import { ClientOnly } from 'remix-utils/client-only';

import { useDisplayMode } from '~/hooks/useDisplayMode';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { RootLoaderData } from '~/root';
import { PillButton } from '~/routes/community/_components/ui/PillButton';
import toast from '~/utils/toast';

import Button from '../Button';
import type { PwaPromptBranding } from './InstallPrompt';
import { isStandaloneDisplayMode, PWA_PROMPT_STORAGE_KEYS } from './pwaPromptConfig';
import { useEnablePushNotification } from './useEnablePushNotification';
import { usePromptDismissal } from './usePromptDismissal';

type EnablePushNotificationPromptProps = {
  debug?: boolean;
  storageKey?: string;
  branding?: PwaPromptBranding;
  variant?: 'default' | 'community';
};

const EnablePushNotificationPrompt = ({
  debug = false,
  storageKey = PWA_PROMPT_STORAGE_KEYS.publicationPush,
  branding,
  variant = 'default',
}: EnablePushNotificationPromptProps) => {
  return (
    <ClientOnly>
      {() => (
        <EnablePushNotificationPromptImpl debug={debug} storageKey={storageKey} branding={branding} variant={variant} />
      )}
    </ClientOnly>
  );
};

function EnablePushNotificationPromptImpl({
  debug = false,
  storageKey = PWA_PROMPT_STORAGE_KEYS.publicationPush,
  branding,
  variant = 'default',
}: EnablePushNotificationPromptProps) {
  const isLoggedIn = useIsLoggedIn();
  const displayMode = useDisplayMode();
  const isInstalled = isStandaloneDisplayMode(displayMode);
  const { onEnable } = useEnablePushNotification();
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const publication = rootData?.site?.publication;
  const resolvedBranding: PwaPromptBranding = branding ?? {
    name: publication?.name ?? '',
    logoUrl: publication?.logo?.thumb?.url ?? '',
  };
  const { t } = useTranslation();
  const [promptShown, setPromptShown] = usePromptDismissal(storageKey);
  const [showPrompt, setShowPrompt] = useState(!promptShown && isLoggedIn);
  const isCommunityVariant = variant === 'community';

  const onEnablePushNotification = async () => {
    setShowPrompt(false);
    const enabled = await onEnable();
    // Only persist the dismissal when it actually worked; on failure keep the
    // prompt available so the member can retry ot dismiss it
    if (enabled) {
      setPromptShown(true);
    } else {
      setShowPrompt(true);
    }
  };

  const onClose = () => {
    toast(
      isCommunityVariant
        ? t('community.push.enable_from_settings')
        : t('pwa.enable_push_notification_prompt.enable_push_notifications_from_menu'),
      { icon: 'ℹ️' },
    );

    setShowPrompt(false);
    setPromptShown(true);
  };

  if (!resolvedBranding.logoUrl || !resolvedBranding.name) return null;
  if (!debug && (promptShown || !isInstalled || !isLoggedIn)) return null;
  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(0,0,0,0.5)]">
      <div
        className={`absolute top-0 w-full rounded-b-xl border p-6 ${
          isCommunityVariant
            ? 'border-community-primary/20 bg-community-bg text-community-text-on-bg'
            : 'bg-white text-gray-950'
        }`}
      >
        <div className="flex gap-6">
          <img
            src={resolvedBranding.logoUrl}
            alt={resolvedBranding.name}
            className="h-16 w-16 flex-none overflow-clip rounded-lg"
          />
          <div className="flex flex-col gap-6">
            <p>{t('pwa.enable_push_notification_prompt.show_notifications_latest_updates')}</p>
            <div className="flex justify-end gap-4">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('pwa.enable_push_notification_prompt.maybe_later')}
              </Button>
              {isCommunityVariant ? (
                <PillButton type="button" variant="primary" onClick={onEnablePushNotification}>
                  {t('pwa.enable_push_notification_prompt.enable')}
                </PillButton>
              ) : (
                <Button type="button" variant="primary" onClick={onEnablePushNotification}>
                  {t('pwa.enable_push_notification_prompt.enable')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EnablePushNotificationPrompt;
